import { useState } from 'react';
import { ReactComponent as ChevronDown } from '@assets/icons/chevron-down.svg';
import parse from 'html-react-parser';

import { Box, Collapse, Flex, Stack, Text, useTheme } from '@chakra-ui/react';

import { ExtendedAddonInterface, ServiceInterface } from 'types';

import { Button, Divider } from 'ui';
import { ServiceItem } from '../ServiceItem';

interface ServiceAccordionItemInterface {
  services: ServiceInterface[];
  selectedServices: ServiceInterface[];
  onClick: (service: ServiceInterface) => void;
  name: string;
}

const ServiceItemAccordion = ({
  services,
  selectedServices,
  name,
  onClick
}: ServiceAccordionItemInterface) => {
  const {
    colors: {
      palette: {
        booking: {
          stepService: {
            serviceList: { serviceItemAccordion }
          }
        }
      }
    }
  } = useTheme();

  const [isOpen, setIsOpen] = useState(false);

  const handleClickService = (
    service: ServiceInterface | ExtendedAddonInterface
  ) => {
    onClick(service as ServiceInterface);
  };

  const toggleAccordion = () => {
    setIsOpen((prevState) => !prevState);
  };

  return (
    <Stack
      gap={0}
      className='item-accordion'
      sx={{
        my: '3px',
        '&:first-of-type': {
          marginTop: 0
        },
        '&:last-of-type': {
          marginBottom: 0
        }
      }}
    >
      <Flex
        position='relative'
        justifyContent='space-between'
        alignItems='center'
        pl={{ base: '20px', md: '25px' }}
        pr={{ base: '8px', md: '12px' }}
        py={{ base: '18px', md: '30px' }}
        bg={
          isOpen
            ? serviceItemAccordion.openBackground
            : serviceItemAccordion.closedBackground
        }
        w='100%'
      >
        <Button
          w='100%'
          h='100%'
          position='absolute'
          top={0}
          right={0}
          bottom={0}
          left={0}
          onClick={toggleAccordion}
          variant='text'
          borderRadius={0}
          aria-label='toggle accordion'
          zIndex={2}
          opacity='0'
        >
          Toggle Accordion
        </Button>
        <Text
          fontSize='18px'
          fontWeight={{ base: 'normal', md: 'semibold' }}
          color={
            isOpen
              ? serviceItemAccordion.openTextColor
              : serviceItemAccordion.closedTextColor
          }
          mb='5px'
        >
          {parse(name ?? '')}
        </Text>
        <Button
          w='auto'
          fontSize='12px'
          color={
            isOpen
              ? serviceItemAccordion.openTextColor
              : serviceItemAccordion.closedTextColor
          }
          variant='text'
          aria-label='toggle services'
          flexShrink={0}
        >
          View Services
          <ChevronDown
            width={16}
            height={18}
            style={{
              marginLeft: '10px',
              marginRight: '0',
              transition: 'transform .2s',
              fill: isOpen
                ? serviceItemAccordion.openChevronColor
                : serviceItemAccordion.closedChevronColor,
              transform: isOpen ? 'rotate(-180deg)' : 'rotate(0)'
            }}
          />
        </Button>
      </Flex>

      <Collapse in={isOpen}>
        <Box bg={serviceItemAccordion.serviceBackground} position='relative'>
          <Divider
            position='absolute'
            bg={serviceItemAccordion.dividerColor}
            top={0}
            height='1px'
            opacity='0.23'
          />
          {services.map((service, index) => {
            const isLastItem = index === services.length - 1;

            return (
              <ServiceItem
                key={service.id}
                service={service}
                onClick={handleClickService}
                isChecked={selectedServices.includes(service)}
                isLastItem={isLastItem}
                isAddonVariant={false}
              />
            );
          })}
        </Box>
      </Collapse>
    </Stack>
  );
};

export default ServiceItemAccordion;
